/**
 * react-hook-form の `onInvalid` が渡す nested な `FieldErrors` tree を再帰走査し、
 * Zod の `message` 文字列を重複排除して返す共通 helper。
 *
 * - field path は出力に含めない (= `bigSteps[0].title` のような内部表現をユーザーに
 *   見せない。 Zod の message 文字列は単独で読める日本語である前提)。
 * - react-hook-form の内部 field (`message` / `type` / `ref`) は walk 対象から
 *   除外する (= `ref` は DOM 要素を指すため、 走査すると無関係な値を拾う)。
 * - 同一 message が複数の nested 位置で出た場合は dedupe する。
 *
 * 引数は `unknown`: form が複数 schema の union を扱う等で `FieldErrors<T>` の具象型が
 * 一意に定まらない呼び出し側があり、 内部で構造的に判定する方が型横断で安全なため
 * (= 各 form で hand-roll されていた実装も同じ理由で `unknown` を採用していた)。
 *
 * 呼び出し側は戻りを `messages.length > 0 ? messages.join('\n') : t('...validation')`
 * のように top-level alert (`root.serverError`) へ集約する。
 *
 * 下部 alert に出す文言の決定には直接使わず `resolveFormErrorAlertMessage` を使う
 * (= 項目脇に出るエラーを alert に再掲しないため)。本 helper は「エラーが 1 件でも
 * あるか」の判定や、alert 以外の集約 (toast 等) で使う。
 */
export function collectFormErrorMessages(errors: unknown): string[] {
  const messages: string[] = []
  const walk = (node: unknown): void => {
    if (node === null || node === undefined) return
    if (typeof node !== 'object') return
    if (
      'message' in node &&
      typeof (node as { message?: unknown }).message === 'string'
    ) {
      const msg = (node as { message: string }).message
      if (msg !== '') messages.push(msg)
    }
    for (const key of Object.keys(node)) {
      if (key === 'message' || key === 'type' || key === 'ref') continue
      walk((node as Record<string, unknown>)[key])
    }
  }
  walk(errors)
  return Array.from(new Set(messages))
}

/**
 * `onInvalid` 時に **下部 alert (`root.serverError`) へ出すべき文言** を決める。
 * 出す必要が無ければ `undefined` を返す。
 *
 * 判定:
 * 1. **nested error (深さ 2 以上) がある** → その message を alert に出す。
 *    配列要素や入れ子オブジェクトのエラーは項目脇の `FieldErrorText` に出ない経路が
 *    あるため (例: `bigSteps[0].title`)、集約しないと silent fail になる。
 * 2. **項目直下 (深さ 1) のエラーだけ** → `undefined`。`<FieldRoot invalid>` +
 *    `<FieldErrorText>` が各項目の脇に同じ文言を出しており、alert は**重複表示**になる
 *    (2026-08-06 デザイナー指摘「form エラーを表示する際は error alert 重複不要」)。
 * 3. **message が 1 件も無い** → `fallbackMessage`。文言の無いエラー (= 画面上どこにも
 *    出ない) で「保存ボタンが効かない」ように見える事故を防ぐ、§1 本来の silent fail 対策。
 *
 * 深さで切るのは §1 の理由そのままで、同節が集約の根拠に挙げているのが
 * 「nested error は個別 `FieldErrorText` に隠れて top-level に伝搬しない」ことだから。
 * 項目直下のエラーはその前提に当たらない。
 */
export function resolveFormErrorAlertMessage(
  errors: unknown,
  fallbackMessage: string,
): string | undefined {
  const nested: string[] = []
  let hasAnyMessage = false

  const walk = (node: unknown, depth: number): void => {
    if (node === null || node === undefined) return
    if (typeof node !== 'object') return
    if (
      'message' in node &&
      typeof (node as { message?: unknown }).message === 'string'
    ) {
      const msg = (node as { message: string }).message
      if (msg !== '') {
        hasAnyMessage = true
        // depth 1 = `errors.<field>` (項目直下)。 2 以上が nested。
        if (depth >= 2) nested.push(msg)
      }
    }
    for (const key of Object.keys(node)) {
      if (key === 'message' || key === 'type' || key === 'ref') continue
      walk((node as Record<string, unknown>)[key], depth + 1)
    }
  }
  walk(errors, 0)

  if (nested.length > 0) return Array.from(new Set(nested)).join('\n')
  if (!hasAnyMessage) return fallbackMessage
  return undefined
}
